import React, { useState, useEffect, useMemo } from 'react';
import { X, Trophy, Medal, Loader2, RefreshCw, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { fetchLeaderboard } from '../services/leaderboard';
import { UserProfile } from '../lib/progression';
import { cn } from '../lib/utils';
import { useSettings } from './SettingsContext';

interface LeaderboardModalProps {
  onClose: () => void;
  myUid?: string;
  myProfile?: UserProfile | null;
  isAnonymous?: boolean;
  onOpenAuth?: () => void;
}

type TabType = 'xp' | 'won' | 'winrate';

export default function LeaderboardModal({ 
  onClose, 
  myUid, 
  myProfile,
  isAnonymous,
  onOpenAuth
}: LeaderboardModalProps) {
  const { playSfx, triggerHaptic } = useSettings();
  const [players, setPlayers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Tab and Search States
  const [activeTab, setActiveTab] = useState<TabType>('xp');
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLeaderboard();
      setPlayers(data);
    } catch (err) {
      setError('Impossibile caricare la classifica. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Process, filter, and sort players client-side
  const processedPlayers = useMemo(() => {
    let result = [...players];

    // 1. Search Query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(p => p.displayName.toLowerCase().includes(query));
    }

    // 2. Tab-specific filtering and sorting
    if (activeTab === 'xp') {
      // Sort by XP descending (natively provided, but double checked)
      result.sort((a, b) => b.xp - a.xp);
    } else if (activeTab === 'won') {
      // Sort by wins descending
      result.sort((a, b) => b.stats.won - a.stats.won);
    } else if (activeTab === 'winrate') {
      // Filter out players with less than 5 games played for statistical significance
      result = result.filter(p => p.stats.played >= 5);
      // Sort by win rate descending
      result.sort((a, b) => {
        const wrA = a.stats.played > 0 ? a.stats.won / a.stats.played : 0;
        const wrB = b.stats.played > 0 ? b.stats.won / b.stats.played : 0;
        return wrB - wrA;
      });
    }

    return result;
  }, [players, activeTab, searchQuery]);

  // Determine current player's rank in the processed list
  const myRankInList = useMemo(() => {
    if (!myUid) return null;
    const idx = processedPlayers.findIndex(p => p.uid === myUid);
    return idx !== -1 ? idx + 1 : null;
  }, [processedPlayers, myUid]);

  const getRankBadge = (rank: number) => {
    if (rank === 1) {
      return (
        <div className="w-8 h-8 rounded-full bg-yellow-400 text-black flex items-center justify-center font-black text-sm shadow-lg shadow-yellow-400/25">
          <Trophy className="w-4 h-4" />
        </div>
      );
    }
    if (rank === 2) {
      return (
        <div className="w-8 h-8 rounded-full bg-slate-300 text-black flex items-center justify-center font-black text-sm shadow-lg shadow-slate-300/20">
          <Medal className="w-4 h-4" />
        </div>
      );
    }
    if (rank === 3) {
      return (
        <div className="w-8 h-8 rounded-full bg-amber-600 text-white flex items-center justify-center font-black text-sm shadow-lg shadow-amber-600/20">
          <Medal className="w-4 h-4" />
        </div>
      );
    }
    return (
      <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 text-white/50 flex items-center justify-center font-bold text-xs">
        {rank}
      </div>
    );
  };

  const handleTabChange = (tab: TabType) => {
    playSfx('click');
    triggerHaptic('light');
    setActiveTab(tab);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.95, y: 15, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 15, opacity: 0 }}
          className="w-full max-w-lg glass-panel rounded-3xl relative border border-white/10 flex flex-col max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] overflow-hidden shadow-2xl my-4 sm:my-6"
        >
        {/* Header */}
        <div className="p-5 sm:p-6 pb-3 border-b border-white/5 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-2.5">
            <Trophy className="w-6 h-6 text-yellow-400 animate-pulse" />
            <h2 className="text-xl sm:text-2xl font-black uppercase text-yellow-400 tracking-wider" style={{ fontFamily: '"Playfair Display", serif' }}>
              Classifica
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                playSfx('click');
                triggerHaptic('light');
                loadData();
              }}
              disabled={loading}
              className="p-2 hover:bg-white/10 rounded-full transition-colors disabled:opacity-50"
              title="Ricarica"
            >
              <RefreshCw className={cn("w-4 h-4 text-white/60", loading && "animate-spin")} />
            </button>
            <button
              onClick={() => {
                playSfx('click');
                triggerHaptic('light');
                onClose();
              }}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>
        </div>

        {/* Tab Controls & Search Bar */}
        <div className="px-5 sm:px-6 pt-3 pb-2 space-y-3 shrink-0">
          {/* Tab Selector */}
          <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
            <button
              onClick={() => handleTabChange('xp')}
              className={cn(
                "flex-1 py-2 text-center text-xs font-bold rounded-lg transition-all",
                activeTab === 'xp'
                  ? "bg-yellow-500 text-black shadow-md font-black"
                  : "text-white/60 hover:text-white/90"
              )}
            >
              XP / Livello
            </button>
            <button
              onClick={() => handleTabChange('won')}
              className={cn(
                "flex-1 py-2 text-center text-xs font-bold rounded-lg transition-all",
                activeTab === 'won'
                  ? "bg-yellow-500 text-black shadow-md font-black"
                  : "text-white/60 hover:text-white/90"
              )}
            >
              Vittorie
            </button>
            <button
              onClick={() => handleTabChange('winrate')}
              className={cn(
                "flex-1 py-2 text-center text-xs font-bold rounded-lg transition-all",
                activeTab === 'winrate'
                  ? "bg-yellow-500 text-black shadow-md font-black"
                  : "text-white/60 hover:text-white/90"
              )}
            >
              Win Rate %
            </button>
          </div>

          {/* Search Field */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              placeholder="Cerca giocatore per nome..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-black/40 border border-white/5 hover:border-white/10 focus:border-yellow-500/50 rounded-xl text-xs text-white placeholder-white/20 focus:outline-none transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Guest Warning CTA */}
        {isAnonymous && !loading && (
          <div className="px-5 sm:px-6 py-1 shrink-0">
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-3 flex items-center justify-between gap-3 shadow-lg">
              <div className="flex-1 text-left">
                <p className="text-yellow-400 font-black text-[9px] sm:text-[10px] uppercase tracking-wider mb-0.5">⚠️ Profilo Ospite Temporaneo</p>
                <p className="text-white/70 text-[10px] sm:text-xs leading-tight">
                  I tuoi progressi in classifica non sono al sicuro. Registrati gratis per salvare il tuo nome e livello!
                </p>
              </div>
              <button
                onClick={() => {
                  playSfx('click');
                  triggerHaptic('medium');
                  onOpenAuth?.();
                }}
                className="bg-yellow-500 hover:bg-yellow-400 text-black font-black uppercase text-[9px] sm:text-[10px] px-3 py-1.5 rounded-xl tracking-wider transition-all whitespace-nowrap active:scale-95 cursor-pointer shrink-0"
              >
                Registrati
              </button>
            </div>
          </div>
        )}

        {/* List Content */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-4 scrollbar-thin">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-3">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
              <p className="text-white/50 text-xs">Caricamento classifica...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-4 text-center">
              <p className="text-red-400 font-medium text-xs">{error}</p>
              <button
                onClick={loadData}
                className="px-5 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all text-xs font-bold"
              >
                Riprova
              </button>
            </div>
          ) : processedPlayers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-white/40">
              <p className="text-xs">Nessun giocatore registrato in questa classifica.</p>
              {activeTab === 'winrate' && (
                <p className="text-[10px] text-white/30 mt-1">Richiede almeno 5 partite giocate.</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {activeTab === 'winrate' && (
                <p className="text-[9px] text-white/30 text-center uppercase tracking-wider mb-2">
                  Classifica dei giocatori con almeno 5 partite giocate
                </p>
              )}
              {processedPlayers.map((player, idx) => {
                const rank = idx + 1;
                const isMe = myUid && player.uid === myUid;
                const winRate = player.stats.played > 0
                  ? Math.round((player.stats.won / player.stats.played) * 100)
                  : 0;

                return (
                  <div
                    key={player.uid}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-2xl transition-all border",
                      isMe
                        ? "glass-panel-amber border-yellow-500/30 font-bold scale-[1.01] shadow-[0_4px_12px_rgba(234,179,8,0.15)]"
                        : "bg-white/5 border-white/5 hover:border-white/15"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {getRankBadge(rank)}
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className={cn("text-xs sm:text-sm font-semibold", isMe ? "text-yellow-300" : "text-white")}>
                            {player.displayName}
                          </span>
                          {isMe && (
                            <span className="text-[8px] bg-yellow-500 text-black px-1.5 py-0.5 rounded-full font-black uppercase tracking-wider">
                              Tu
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-white/40 flex items-center gap-2 mt-0.5 font-medium">
                          <span>Lvl {player.level}</span>
                          <span>•</span>
                          <span>{player.xp} XP</span>
                        </div>
                      </div>
                    </div>

                    <div className="text-right flex flex-col items-end gap-0.5">
                      <div className="text-xs sm:text-sm font-black text-white/90">
                        {player.stats.won} <span className="text-[9px] font-normal text-white/40">Vittorie</span>
                      </div>
                      <div className="text-[9px] text-white/40">
                        {player.stats.played} Partite ({winRate}% WR)
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sticky Footer: "La tua posizione" */}
        {myProfile && !loading && (
          <div className="p-4 bg-gradient-to-t from-black to-black/90 border-t border-white/10 shrink-0">
            {(() => {
              const myTotalGames = myProfile.stats.played;
              const myWinRate = myTotalGames > 0 ? Math.round((myProfile.stats.won / myTotalGames) * 100) : 0;
              
              // Find if I have a rank in this specific view
              const displayRank = myRankInList !== null ? `#${myRankInList}` : (activeTab === 'winrate' && myTotalGames < 5 ? 'N.D.' : '#100+');

              return (
                <div className="glass-panel-amber p-3 rounded-2xl border border-yellow-500/30 flex items-center justify-between shadow-[0_0_15px_rgba(234,179,8,0.1)]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-yellow-500 text-black flex items-center justify-center font-black text-xs shadow-md">
                      {displayRank}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-black text-yellow-300">La tua posizione</span>
                        <span className="text-[8px] bg-white/10 text-white/60 px-1 py-0.2 rounded font-bold uppercase tracking-wider">
                          Profilo
                        </span>
                      </div>
                      <div className="text-[10px] text-white/50 flex items-center gap-2 mt-0.5 font-bold uppercase tracking-wider">
                        <span>Lvl {myProfile.level}</span>
                        <span>•</span>
                        <span>{myProfile.xp} XP</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right flex flex-col items-end gap-0.5">
                    <div className="text-xs font-black text-white">
                      {myProfile.stats.won} <span className="text-[9px] font-normal text-white/40">Vittorie</span>
                    </div>
                    <div className="text-[9px] text-white/40">
                      {myTotalGames} Partite ({myWinRate}% WR)
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        </motion.div>
      </div>
    </div>
  );
}
